import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, RefreshCw, ChevronRight } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { useDiscreet } from "@/context/DiscreetContext";

function Maybe({ children, className = "" }) {
  const { discreet } = useDiscreet();
  return <span className={`${discreet ? "discreet-blur" : ""} ${className}`}>{children}</span>;
}

/**
 * Self-Insight Dashboard — the user-facing Layer 4 surface of the
 * calibration architecture. Editorial, observational, never clinical.
 */
export default function SelfInsight() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [evolution, setEvolution] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/me/insights").then((r) => setData(r.data)).catch((e) => setError(e?.response?.status === 401 ? "" : "Couldn't load your insight."));
    api.get("/me/archetype/history").then((r) => { setHistory(r.data?.history || []); setEvolution(r.data || null); }).catch(() => {});
  };
  useEffect(load, []);

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-cocoa/60 text-sm">Reading your patterns…</p>
        {error && <p className="mt-2 text-mauve text-sm">{error}</p>}
      </div>
    );
  }

  if (!data.has_calibration) {
    return (
      <div data-testid="self-insight-empty" className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Self-Insight</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-cocoa mt-3">Calibrate first, then we'll begin to read you.</h1>
        <p className="text-cocoa/70 mt-3 max-w-md mx-auto leading-relaxed">
          A short, reflective conversation — about 4 minutes. It gives SugarSense the starting context to read your relationship patterns more accurately.
        </p>
        <Link to="/calibration" data-testid="self-insight-calibrate-cta" className="inline-flex ss-pill ss-pill-gold h-11 mt-6">
          <Sparkles size={14} /> Begin calibration
        </Link>
      </div>
    );
  }

  const conf = Math.round((data.confidence || 0) * 100);
  const calConf = Math.round((data.calibration_confidence || 0) * 100);
  const longConf = Math.round((data.longitudinal_confidence || 0) * 100);
  const p = data.primary;

  return (
    <div data-testid="self-insight-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <SeoHead title="Your relationship intelligence · SugarSense" description="A longitudinal read of how you tend to operate in relationships." />

      {/* Hero */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Self-Insight</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-cocoa mt-2 leading-tight">
            How you tend to operate.
          </h1>
          <p className="mt-3 text-cocoa/65 max-w-2xl text-[14px] leading-relaxed">
            An evolving observational read — refined every time SugarSense sees a pattern. Not a label. A working map.
          </p>
        </div>
        <Link to="/calibration" data-testid="self-insight-refine" className="ss-pill h-10 text-xs">
          <RefreshCw size={12} /> Refine calibration
        </Link>
      </div>

      {/* Primary archetype card */}
      {p && (
        <div data-testid="self-insight-primary" className="ss-card-premium p-6 sm:p-8 mb-6">
          <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">Primary read</p>
          <h2 className="font-heading text-3xl text-cocoa leading-tight">{p.name}</h2>
          <p className="text-cocoa/65 italic mt-1">{p.tagline}</p>
          <p className="text-cocoa/85 text-[15px] leading-relaxed mt-4">{p.tendency}</p>

          {p.security_anchors?.length > 0 && (
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mb-2">What helps you feel secure</p>
                <ul className="space-y-1.5 text-cocoa/80 text-[13px]">
                  {p.security_anchors.map((a, i) => <li key={i} className="flex items-start gap-1.5"><span className="text-mauve">·</span>{a}</li>)}
                </ul>
              </div>
              {p.growth_invitation && (
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55 mb-2">A quiet invitation</p>
                  <p className="text-cocoa/75 text-[13px] italic leading-relaxed">{p.growth_invitation}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confidence + signal coverage */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <Tile label="Overall confidence" value={`${conf}%`} sub="Combined" />
        <Tile label="From calibration" value={`${calConf}%`} sub={`${data.observed_signal_count ? "" : "Initial"}`} />
        <Tile label="From observation" value={`${longConf}%`} sub={`${data.observed_signal_count} signals`} />
      </div>

      {/* Secondary + tertiary */}
      {(data.secondary || data.tertiary) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {data.secondary && (
            <div data-testid="self-insight-secondary" className="ss-card p-5">
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-1">Secondary influence</p>
              <p className="font-heading text-lg text-cocoa">{data.secondary.name}</p>
              <p className="text-cocoa/65 text-[12px] mt-1 italic">{data.secondary.tagline}</p>
            </div>
          )}
          {data.tertiary && (
            <div data-testid="self-insight-tertiary" className="ss-card p-5">
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-1">Tertiary thread</p>
              <p className="font-heading text-lg text-cocoa">{data.tertiary.name}</p>
              <p className="text-cocoa/65 text-[12px] mt-1 italic">{data.tertiary.tagline}</p>
            </div>
          )}
        </div>
      )}

      {/* Surfaced dimensions */}
      {data.dimensions?.length > 0 && (
        <section className="mb-8">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Tendencies we've observed</p>
              <h2 className="font-heading text-2xl text-cocoa mt-1">The dimensions that shape your read.</h2>
            </div>
          </div>
          <div className="ss-card p-5">
            <ul className="space-y-3">
              {data.dimensions.map((d) => (
                <li key={d.key} data-testid={`self-insight-dim-${d.key}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-[13px] text-cocoa">{d.label}</span>
                    <span className="text-[11px] text-cocoa/50 tabular-nums">{Math.round(d.score)}%</span>
                  </div>
                  <div className="h-1.5 bg-champagne rounded-full overflow-hidden">
                    <div className="h-full bg-mauve" style={{ width: `${Math.min(100, d.score)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Divergence — only when observed substantially differs from self-reported */}
      {data.divergence?.length > 0 && (
        <section className="mb-8">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Patterns you may not notice</p>
              <h2 className="font-heading text-2xl text-cocoa mt-1">What your behavior says — quietly.</h2>
            </div>
          </div>
          <div className="space-y-2">
            {data.divergence.map((d, i) => (
              <div key={i} data-testid={`self-insight-divergence-${i}`} className="ss-card p-4 flex items-start gap-3">
                <TrendingUp size={14} className="text-mauve mt-1 shrink-0" />
                <div className="flex-1">
                  <p className="text-[13px] text-cocoa leading-relaxed">
                    On <Maybe>{d.dim_key.replace(/_/g, " ")}</Maybe>, you described yourself around <span className="font-medium">{Math.round(d.self_reported)}</span>, but the patterns we're observing read closer to <span className="font-medium">{Math.round(d.observed)}</span>. Worth a quiet reflection.
                  </p>
                  <p className="text-[11px] text-cocoa/50 mt-1">{d.observation_count} observations · refined longitudinally</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Evolution — change points only, once the system has learnt enough */}
      {evolution && (
        <section className="mb-8" data-testid="self-insight-evolution">
          <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-3">How your read has evolved</p>
          {!evolution.ready ? (
            <div className="ss-card p-5" data-testid="evolution-learning">
              <p className="text-[13px] text-cocoa leading-relaxed">SugarSense is still getting to know you. Your evolution timeline appears once there's enough to say something honest — usually after a few advisor reads or a calibration.</p>
              <div className="mt-3 h-1.5 rounded-full bg-champagne overflow-hidden"><div className="h-full bg-mauve/70 transition-all" style={{ width: `${Math.min(100, (100 * (evolution.signals || 0)) / (evolution.signals_needed || 3))}%` }} /></div>
              <p className="mt-1.5 text-[11px] text-cocoa/50">{Math.min(evolution.signals || 0, evolution.signals_needed)} of {evolution.signals_needed} signals observed</p>
            </div>
          ) : (
            <div className="ss-card p-4 space-y-3">
              {history.slice(0, 6).map((h) => (
                <div key={h.id} className="flex gap-3 text-[13px]" data-testid={`evolution-${h.kind}`}>
                  <span className="text-cocoa/50 tabular-nums shrink-0 w-24">{new Date(h.computed_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
                  <p className="text-cocoa leading-relaxed">
                    {h.kind === "first_read" && <>Your first read settled on <Maybe><span className="font-medium">{h.primary_name}</span></Maybe> at {Math.round(h.confidence * 100)}% confidence.</>}
                    {h.kind === "shift" && <>Your read shifted from <Maybe>{h.previous_name}</Maybe> to <Maybe><span className="font-medium">{h.primary_name}</span></Maybe> ({Math.round(h.confidence * 100)}% confidence) after {h.observed_signal_count} observations.</>}
                    {h.kind === "confidence" && <>Still reading as <Maybe><span className="font-medium">{h.primary_name}</span></Maybe> — confidence moved from {Math.round((h.previous_confidence || 0) * 100)}% to {Math.round(h.confidence * 100)}%.</>}
                  </p>
                </div>
              ))}
              <p className="text-[11px] text-cocoa/45 pt-1">Only real changes are shown — repeated identical reads are folded away.</p>
            </div>
          )}
        </section>
      )}

      <div className="mt-8 ss-card-premium p-6 text-center">
        <p className="text-[10px] tracking-[0.4em] uppercase text-mauve mb-2">Provider-independent IP</p>
        <p className="font-heading italic text-lg text-cocoa">
          This profile lives in SugarSense — yours alone, exportable, deletable.
        </p>
        <Link to="/intelligence" className="inline-flex items-center gap-1 mt-3 text-[12px] text-mauve hover:text-cocoa underline">
          See your full relationship intelligence <ChevronRight size={12} />
        </Link>
      </div>
    </div>
  );
}

function Tile({ label, value, sub }) {
  return (
    <div className="ss-card p-4">
      <p className="text-[10px] tracking-[0.3em] uppercase text-mauve mb-1">{label}</p>
      <p className="font-heading text-2xl text-cocoa leading-none">{value}</p>
      {sub && <p className="text-[11px] text-cocoa/55 mt-1">{sub}</p>}
    </div>
  );
}
